import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Dialog } from '@/components/ui/Dialog';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
    useAdminFeedback,
    useUpdateFeedbackStatus,
    useDeleteFeedback,
} from '@/hooks/admin/useAdminFeedback';
import type { AdminFeedback, FeedbackStatus } from '@/api/types';
import {
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChatBubbleLeftIcon,
    TrashIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';

const STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = [
    { value: 'new', label: 'New' },
    { value: 'read', label: 'Read' },
    { value: 'resolved', label: 'Resolved' },
];

function getStatusColor(status: string): string {
    switch (status) {
        case 'new':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
        case 'read':
            return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
        case 'resolved':
            return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
}

function FeedbackRow({
    feedback,
    onStatusChange,
    onDelete,
    onViewMessage,
    isUpdating,
}: {
    feedback: AdminFeedback;
    onStatusChange: (id: number, status: FeedbackStatus) => void;
    onDelete: (feedback: AdminFeedback) => void;
    onViewMessage: (feedback: AdminFeedback) => void;
    isUpdating: boolean;
}) {
    const date = new Date(feedback.createdAt);

    return (
        <tr className="border-b border-gray-200 dark:border-gray-700">
            <td className="px-4 py-3 font-mono text-sm text-gray-500 dark:text-gray-400">
                #{feedback.id}
            </td>
            <td className="px-4 py-3 text-gray-900 dark:text-white">
                {feedback.userEmail}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {feedback.reasonLabel}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                <button
                    type="button"
                    onClick={() => onViewMessage(feedback)}
                    className="max-w-xs truncate text-left hover:text-blue-600 hover:underline dark:hover:text-blue-400"
                    title="Click to view full message"
                >
                    {feedback.messagePreview}
                </button>
            </td>
            <td className="px-4 py-3">
                <span
                    className={`rounded-sm px-2 py-1 text-xs font-medium ${getStatusColor(feedback.status)}`}
                >
                    {feedback.statusLabel}
                </span>
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {date.toLocaleDateString()} {date.toLocaleTimeString()}
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onViewMessage(feedback)}
                        className="rounded-sm p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        title="View message"
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <div className="w-28">
                        <Select
                            value={feedback.status}
                            onChange={(newStatus) =>
                                onStatusChange(feedback.id, newStatus)
                            }
                            options={STATUS_OPTIONS}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => onDelete(feedback)}
                        disabled={isUpdating}
                        className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                        title="Delete"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

export function AdminFeedbackPage() {
    usePageTitle('Admin - Feedback');

    const [page, setPage] = useState(1);
    const limit = 20;

    const { data, isLoading } = useAdminFeedback(page, limit);
    const updateStatus = useUpdateFeedbackStatus();
    const deleteFeedback = useDeleteFeedback();

    const [deleteTarget, setDeleteTarget] = useState<AdminFeedback | null>(
        null
    );
    const [viewTarget, setViewTarget] = useState<AdminFeedback | null>(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

    const handleStatusChange = (feedbackId: number, status: FeedbackStatus) => {
        updateStatus.mutate(
            { feedbackId, status },
            {
                onSuccess: () => toast.success('Status updated'),
                onError: () => toast.error('Failed to update status'),
            }
        );
    };

    const handleDelete = (feedback: AdminFeedback) => {
        setDeleteTarget(feedback);
    };

    const handleViewMessage = (feedback: AdminFeedback) => {
        setViewTarget(feedback);
        setIsViewDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        deleteFeedback.mutate(deleteTarget.id, {
            onSuccess: () => {
                toast.success('Feedback deleted');
                setDeleteTarget(null);
            },
            onError: () => {
                toast.error('Failed to delete feedback');
            },
        });
    };

    if (isLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-4">
                <Link
                    to="/admin"
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Admin
                </Link>
            </div>

            <PageHeader title="Feedback Submissions" />

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-secondary">
                {data?.feedback && data.feedback.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Ticket #
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            User
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Reason
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Message
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Date
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.feedback.map((item) => (
                                        <FeedbackRow
                                            key={item.id}
                                            feedback={item}
                                            onStatusChange={handleStatusChange}
                                            onDelete={handleDelete}
                                            onViewMessage={handleViewMessage}
                                            isUpdating={
                                                updateStatus.isPending ||
                                                deleteFeedback.isPending
                                            }
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Showing {(page - 1) * limit + 1} to{' '}
                                {Math.min(page * limit, data.total)} of{' '}
                                {data.total}
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                    Previous
                                </Button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Page {page} of {data.totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= data.totalPages}
                                >
                                    Next
                                    <ChevronRightIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <ChatBubbleLeftIcon className="mb-2 h-8 w-8" />
                        <p>No feedback submissions yet</p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                title="Delete Feedback"
                description={`Are you sure you want to delete this feedback from "${deleteTarget?.userEmail}"? This action cannot be undone.`}
                isLoading={deleteFeedback.isPending}
            />

            {/* Message Detail Dialog */}
            <Dialog
                open={isViewDialogOpen}
                onClose={() => setIsViewDialogOpen(false)}
                afterLeave={() => setViewTarget(null)}
                title={viewTarget ? `Ticket #${viewTarget.id}` : 'Feedback Message'}
                size="lg"
            >
                {viewTarget && (
                    <div className="space-y-4">
                        <div className="grid gap-3 text-sm">
                            <div className="flex gap-2">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    From:
                                </span>
                                <span className="text-gray-900 dark:text-white">
                                    {viewTarget.userEmail}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Reason:
                                </span>
                                <span className="text-gray-900 dark:text-white">
                                    {viewTarget.reasonLabel}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Date:
                                </span>
                                <span className="text-gray-900 dark:text-white">
                                    {new Date(viewTarget.createdAt).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <span className="font-medium text-gray-500 dark:text-gray-400">
                                    Status:
                                </span>
                                <span
                                    className={`rounded-sm px-2 py-0.5 text-xs font-medium ${getStatusColor(viewTarget.status)}`}
                                >
                                    {viewTarget.statusLabel}
                                </span>
                            </div>
                        </div>
                        <div>
                            <p className="mb-2 font-medium text-gray-500 dark:text-gray-400">
                                Message:
                            </p>
                            <div className="whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-gray-900 dark:bg-gray-900 dark:text-white">
                                {viewTarget.message}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
}
